"use client"
import React from 'react'
import { cn } from '@/shared/utils/tw'
import { useLlmModelSelect } from '../model/use-llm-model-select'
import {
  LLM_MODEL_OPTIONS,
  getLlmModelOption,
} from '../const/llm-model-options'

interface LlmModelSelectProps {
  className?: string
  disabled?: boolean
  variant?: 'dark' | 'light'
}

/**
 * LLM 모델 선택 드롭다운
 *
 * 위치: NavigationBar 우측 (LanguageSelect 옆)
 * 스타일: LanguageSelect와 동일한 Liquid Glass
 */
const LlmModelSelect: React.FC<LlmModelSelectProps> = ({
  className,
  disabled = false,
  variant = 'dark',
}) => {
  const isLight = variant === 'light'
  const { isOpen, selectedModelKey, selectRef, toggleDropdown, selectModel } =
    useLlmModelSelect()

  const currentOption = getLlmModelOption(selectedModelKey)

  return (
    <div ref={selectRef} className={cn('relative', className)}>
      {/* 모델 선택 버튼 */}
      <button
        type="button"
        onClick={disabled ? undefined : toggleDropdown}
        disabled={disabled}
        className={cn(
          'flex items-center justify-center',
          'rounded-full',
          'backdrop-blur-[9.4px]',
          '[-webkit-backdrop-filter:blur(9.4px)]',
          'focus:outline-none',
          disabled
            ? 'cursor-not-allowed opacity-60'
            : 'cursor-pointer transition-all duration-200'
        )}
        style={{
          padding: '8px 10px',
          gap: '4px',
          background: isLight ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.15)',
          border: isLight
            ? '1.7px solid rgba(0,0,0,0.1)'
            : '1.7px solid rgba(255,255,255,0.2)',
        }}
        aria-label="LLM 모델 선택"
      >
        {/* 칩(모델) 아이콘 */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={cn('shrink-0', isLight ? 'text-[#222222]' : 'text-white')}
        >
          <rect x="6" y="6" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" />
          <rect x="10" y="10" width="4" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
          <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>

        {/* 모델 이름 (모바일에서는 숨김) */}
        <span
          className={cn(
            'hidden sm:inline font-pretendard font-semibold text-[14px] leading-[1.3] whitespace-nowrap',
            isLight ? 'text-[#222222]' : 'text-white'
          )}
        >
          {currentOption.shortLabel}
        </span>
      </button>

      {/* 드롭다운 메뉴 */}
      {isOpen && !disabled && (
        <div
          className={cn(
            'absolute right-0 z-[9999]',
            'backdrop-blur-[9.4px]',
            '[-webkit-backdrop-filter:blur(9.4px)]',
            'flex flex-col'
          )}
          style={{
            top: 'calc(100% + 4px)',
            minWidth: '180px',
            borderRadius: '16px',
            background: isLight
              ? 'rgba(255,255,255,0.95)'
              : 'rgba(255,255,255,0.15)',
            border: isLight
              ? '1.7px solid rgba(0,0,0,0.1)'
              : '1.7px solid rgba(255,255,255,0.2)',
            padding: '8px',
            gap: '4px',
          }}
        >
          {LLM_MODEL_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={(e) => selectModel(option.key, e)}
              className={cn(
                'w-full text-left',
                'font-pretendard font-semibold text-[14px] leading-[1.3]',
                'rounded-[10px]',
                'transition-all duration-200',
                'focus:outline-none cursor-pointer',
                selectedModelKey === option.key
                  ? isLight
                    ? 'text-[#222222] bg-[rgba(0,0,0,0.06)]'
                    : 'text-white bg-[rgba(255,255,255,0.15)]'
                  : isLight
                    ? 'text-black/50 hover:text-[#222222] hover:bg-[rgba(0,0,0,0.04)]'
                    : 'text-[rgba(255,255,255,0.6)] hover:text-white hover:bg-[rgba(255,255,255,0.08)]'
              )}
              style={{ padding: '6px 10px' }}
            >
              <div>{option.label}</div>
              <div
                className={cn(
                  'font-normal text-[11px]',
                  isLight ? 'text-black/40' : 'text-[rgba(255,255,255,0.5)]'
                )}
              >
                {option.description}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default LlmModelSelect
